import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { apiError, formatZAR, shopApi, useShop } from "./ShopContext";
import { CartLines } from "./CartDrawer";

export default function ShopCheckout() {
  const { customer, cart, cartTotal, clearCart } = useShop();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (customer === false) navigate("/shop/login?next=/shop/checkout", { replace: true });
    if (customer) { setName(customer.name || ""); setPhone(customer.phone || ""); }
  }, [customer, navigate]);

  if (!customer) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#D96B27]" /></div>;
  if (cart.length === 0) return (
    <div className="py-20 text-center" data-testid="checkout-empty">
      <p className="text-slate-500">Your cart is empty.</p>
      <Link to="/shop/products" className="mt-4 inline-block font-semibold text-[#D96B27]">Browse products</Link>
    </div>
  );

  const place = async () => {
    if (phone.replace(/\D/g, "").length < 9) return toast.error("Please enter a valid cellphone number");
    setBusy(true);
    try {
      const { data } = await shopApi.post("/orders", {
        items: cart.map((l) => ({ product_id: l.product_id, quantity: l.quantity })), phone, name, notes: notes || null,
      });
      clearCart();
      navigate(`/shop/order/${data.id}`, { state: { order: data } });
    } catch (e) {
      toast.error(apiError(e, "Could not place your order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]" data-testid="shop-checkout">
      <div className="space-y-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Checkout</h1>
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="mb-3 font-bold text-slate-900">Your details</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">Name<input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" data-testid="checkout-name-input" /></label>
            <label className="text-sm">Cellphone (for WhatsApp updates)<input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" data-testid="checkout-phone-input" /></label>
          </div>
          <label className="mt-3 block text-sm">Notes for the store (optional)<textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full rounded-lg border px-3 py-2" data-testid="checkout-notes-input" /></label>
        </section>
        <section className="flex items-start gap-3 rounded-2xl border-2 border-[#D96B27] bg-[#FEF3EC] p-5" data-testid="checkout-collect-option">
          <Store className="h-6 w-6 shrink-0 text-[#D96B27]" />
          <div><p className="font-bold text-slate-900">Collect in store — FREE</p><p className="text-sm text-slate-600">Pay when you collect: cash, card or your store account. We'll let you know when it's ready.</p></div>
        </section>
      </div>
      <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-5 lg:sticky lg:top-28">
        <h2 className="mb-2 font-bold text-slate-900">Order summary</h2>
        <div className="max-h-80 overflow-y-auto"><CartLines /></div>
        <div className="mt-3 flex justify-between text-lg font-extrabold"><span>Total</span><span data-testid="checkout-total">{formatZAR(cartTotal)}</span></div>
        <p className="mt-1 text-xs text-slate-500">Prices incl. VAT. If a store promotion is cheaper on the day, you pay the lower price.</p>
        <button type="button" onClick={place} disabled={busy} className="mt-4 w-full rounded-full bg-[#D96B27] py-3 font-bold text-white transition-colors hover:bg-[#C2591B] disabled:opacity-60" data-testid="checkout-place-order-btn">
          {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Place order — pay on collection"}
        </button>
      </aside>
    </div>
  );
}
